// Runtime config bootstrap: una sola llamada a /api/runtime-config para:
//   1. Si env=staging → pintar banner amarillo fijo (sirve para que nadie
//      confunda staging con prod al hacer pruebas con datos reales).
//   2. Si hay Sentry DSN → init Sentry browser (chunk separado).
(async () => {
  try {
    const r = await fetch('/api/runtime-config');
    if (!r.ok) return;
    const cfg = await r.json();

    // ── Banner staging ──
    if (cfg?.env === 'staging' && !document.getElementById('staging-banner')) {
      const bar = document.createElement('div');
      bar.id = 'staging-banner';
      bar.textContent = '⚠️ STAGING — datos de prueba, no es producción';
      Object.assign(bar.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        background: '#F59E0B',
        color: '#1a1a1a',
        textAlign: 'center',
        padding: '6px 12px',
        fontSize: '12px',
        fontWeight: '700',
        letterSpacing: '.4px',
        zIndex: '9999',
        boxShadow: '0 1px 0 rgba(0,0,0,.08)',
      });
      document.body.appendChild(bar);
      // Empuja el contenido hacia abajo para que no quede oculto bajo el banner
      document.body.style.paddingTop = '28px';
    }

    // ── Sentry ──
    if (cfg?.sentryDsnPublic) {
      const Sentry = await import('@sentry/browser');
      Sentry.init({
        dsn: cfg.sentryDsnPublic,
        environment: cfg.env || 'production',
        release: cfg.release || 'unknown',
        tracesSampleRate: 0.05,
        integrations: [Sentry.browserTracingIntegration()],
      });
      window.__SENTRY_INITIALIZED__ = true;
      console.log('🛡️ Sentry browser inicializado (' + (cfg.env || 'prod') + ')');
    }
  } catch (e) {
    console.warn('Runtime config init failed:', e?.message || e);
  }
})();

// Helpers compartidos: ver src/common.jsx
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  STD_KEYS,
  KEY_LBL,
  MONTHS,
  ROLE_CAPABILITIES,
  can,
  BADGE_CATS,
  fmt,
  fmtK,
  billingOf,
  billingFromDeals,
  billingFromDealsByMonth,
  getUserTeamForMonth,
  getTeamUserIds,
  getUserRoleForMonth,
  getUserLevelForMonth,
  aggregateHistory,
  hashPin,
  getCatKeys,
  getKeyLabel,
  Ic,
  Toast,
} from './common.jsx';
// Componentes extraídos a archivos por dominio
import { OnboardingModal } from './onboarding.jsx';
import { PasswordSet } from './password-set.jsx';
import { GlobalSearch } from './global-search.jsx';
import { TourOverlay, AlertsBanner } from './tour.jsx';
import { SuggestionsPage } from './suggestions.jsx';
import { ChangeLogPage } from './changelog.jsx';
import { track } from './telemetry.js';
// Componentes extraídos a archivos por dominio
// ─── Páginas EAGER (carga inicial) ───
// Inicio + auth + ventas/cobros + comercial-home + user-switcher van eager:
// la mayoría de sesiones los tocan en el primer render.
import { ManagerHomePage } from './inicio.jsx';
import { SalesPage, CobrosPage, BajasPage, ImpagosPage } from './operativa.jsx';
import { ResetPinPage, LoginPage } from './auth.jsx';
import { UserSwitcher } from './user-switcher.jsx';

// ─── Páginas LAZY (chunk separado, se baja al navegar) ───
// React.lazy + Suspense baja un chunk solo cuando se renderiza por primera vez.
// Esto saca de la carga inicial ~200kb minified: Config (admin), Historial
// (analytics pesado), Hubspot, Calc, Carrera, Equipo, Perfil, Compare, etc.
const { lazy: rLazy, Suspense } = React;
function lazyNamed(loader, name) {
  return rLazy(() => loader().then(m => ({ default: m[name] })));
}
const ConfigPage = lazyNamed(() => import('./config.jsx'), 'ConfigPage');
const UsagePage = lazyNamed(() => import('./pages/usage.jsx'), 'UsagePage');
const CarreraPage = lazyNamed(() => import('./carrera.jsx'), 'CarreraPage');
const HubspotPage = lazyNamed(() => import('./hubspot.jsx'), 'HubspotPage');
const LivePage = lazyNamed(() => import('./hubspot.jsx'), 'LivePage');
const EquipoPage = lazyNamed(() => import('./equipo.jsx'), 'EquipoPage');
const ObjetivosPage = lazyNamed(() => import('./objetivos.jsx'), 'ObjetivosPage');
const CierrePage = lazyNamed(() => import('./cierre.jsx'), 'CierrePage');
const UserProfilePage = lazyNamed(() => import('./perfil.jsx'), 'UserProfilePage');
const MyProfilePage = lazyNamed(() => import('./perfil.jsx'), 'MyProfilePage');
const HistAnalyticsPage = lazyNamed(() => import('./historial.jsx'), 'HistAnalyticsPage');
const RankingPage = lazyNamed(() => import('./historial.jsx'), 'RankingPage');
const HistPage = lazyNamed(() => import('./historial.jsx'), 'HistPage');
const CalcPage = lazyNamed(() => import('./calc.jsx'), 'CalcPage');
const SimPage = lazyNamed(() => import('./calc.jsx'), 'SimPage');
const PlanesPage = lazyNamed(() => import('./planes.jsx'), 'PlanesPage');
// Lote-114: previsualización del Inicio comercial v2. Oculto para
// comerciales reales — solo accesible desde sidebar admin.
const DemoComercialV2Page = lazyNamed(
  () => import('./comercial-home-v2.jsx'),
  'DemoComercialV2Page'
);
// Lote-198: el rediseño v2 pasa a ser el Inicio REAL del comercial.
const CommercialHomeV2Page = lazyNamed(
  () => import('./comercial-home-v2.jsx'),
  'CommercialHomeV2Page'
);

// lote-205: Sugerencias oculto en el sidebar para todos los roles (no borrado).
// Poner a true para reactivar el acceso desde el menú lateral.
const SHOW_SUGGESTIONS_NAV = false;

// ─── Planes y tramos (wrapper con sub-tabs) ───
// lote-205: Planes y Tramos se editan desde el MISMO sitio (antes eran dos
// items de sidebar separados). Una tira de pestañas (Planes | Tramos) encima y
// debajo se renderiza el componente correspondiente tal cual (cada uno conserva
// su propio topbar con sus acciones: "Nuevo plan", añadir tramo, etc.).
//   - Planes  → PlanesPage (maestro-detalle). Visible para Admin/RO/TL.
//   - Tramos  → ConfigPage en modo levels-only. Solo Admin/RO (canSeeTramos).
function PlanesTramosPage(props) {
  const { canSeeTramos = true, initialTab = 'planes' } = props;
  const [tab, setTab] = React.useState(canSeeTramos ? initialTab : 'planes');
  const tabs = canSeeTramos
    ? [
        ['planes', 'Planes'],
        ['tramos', 'Tramos'],
      ]
    : [['planes', 'Planes']];
  return (
    <>
      <div
        style={{
          background: 'var(--wh)',
          borderBottom: '1px solid var(--bd)',
          padding: '0 28px',
          display: 'flex',
          gap: 0,
          overflowX: 'auto',
          flexWrap: 'nowrap',
          flexShrink: 0,
        }}
      >
        {tabs.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '12px 16px',
              fontSize: 13,
              fontWeight: tab === k ? 600 : 500,
              color: tab === k ? 'var(--tx)' : 'var(--tx-mu)',
              borderBottom: `2px solid ${tab === k ? 'var(--pu)' : 'transparent'}`,
              marginBottom: -1,
              whiteSpace: 'nowrap',
            }}
          >
            {l}
          </button>
        ))}
      </div>
      {tab === 'planes' && (
        <PlanesPage
          cats={props.cats}
          reload={props.reload}
          teams={props.teams}
          allUsers={props.allUsers}
          activeUser={props.activeUser}
          canEdit={props.canEditPlanes}
          isSuperAdmin={props.isSuperAdmin}
        />
      )}
      {tab === 'tramos' && canSeeTramos && (
        <ConfigPage
          cats={props.cats}
          reload={props.reload}
          teams={props.teams}
          reloadTeams={props.reloadTeams}
          mode="levels-only"
          activeUser={props.activeUser}
          isReadOnly={props.isReadOnly}
        />
      )}
    </>
  );
}

// ─── Error boundary de página (lote-159) ───
// Contiene un crash de render de cualquier página lazy para que NO tumbe toda
// la app (pantalla en blanco). Se resetea al navegar (resetKey=page).
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error) {
    try {
      window.errorLog?.capture?.(error, { tag: 'page-render', page: this.props.resetKey });
    } catch {
      /* noop */
    }
  }
  componentDidUpdate(prev) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 28, maxWidth: 560, margin: '40px auto', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
          <h3 style={{ margin: '0 0 6px' }}>Algo ha fallado en esta sección</h3>
          <div style={{ fontSize: 13, color: 'var(--tx-mu)', marginBottom: 16 }}>
            El error se ha registrado. Prueba a recargar o cambiar de sección.
          </div>
          <button className="btn-primary" onClick={() => window.location.reload()}>
            Recargar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Save Modal ───

// ─── Category Modal ───

// ─── Reset PIN Page (shown when URL has ?reset_token=...) ───

// ─── Login Page ───

// ─── Equipo (wrapper con sub-tabs) ───
// La página Equipo agrupa:
//   - Configuración: la antigua "Usuarios" (alta/baja/edición de miembros)
//   - Control equipo: matriz comercial × mes con equipo/nivel/plan por mes
// Manager + manager_comercial ven ambas. Comercial verá "Control equipo"
// pero solo su fila (lo veremos en otro PR cuando aterrice en Mi área).

// Matriz comercial × mes mostrando equipo/nivel/plan por mes.
// Manager ve todos los comerciales (filtrados por scope); manager_comercial ve los
// de su equipo. Usa monthlyTeams + monthlyCategories del user para el detalle por
// mes; nivel es global (futuro: monthlyLevels). Año seleccionable arriba.

// ─── UsersPageInner — gestión de miembros del equipo (va dentro de EquipoPage) ───

// ─── User Profile Page ───
// ─── Matriz unificada: histórico mensual del comercial ───
// Sustituye las 5 tabs separadas (Planes, Equipos, Roles, Niveles, Objetivos)
// por una sola tabla con filas = campos y columnas = meses del año seleccionado.

// ─── Vista "Inicio" gamificada dentro del perfil de un comercial ───
// Mostrada como pestaña principal cuando el manager abre el perfil de un comercial,
// o cuando el comercial entra en su propio perfil. Pensada para crecer con insignias.
// Bloque con objetivo del mes + proyección a fin de mes + simulador de
// próxima venta. Aparece en el inicio del comercial.

// ─── Carrera ────────────────────────────────────────────────────────────────
// `computeUserStatsForBadges` y `evaluateUserBadges` viven en common.jsx para
// que carrera.jsx pueda usarlos sin crear circular deps (app.jsx ya importa
// carrera.jsx).

// Sub-componente: Planes activos

// Sub-componente: Niveles

// Sub-componente: Insignias (con Hall of Fame)

// ─── Analytics Page (manager only) ───

// ─── Config Page ───

// ─── Calculator Page ───

// ─── History Page ───
// Wrapper que une Historial + Estadísticas en una sola página con dos tabs y
// filtros compartidos arriba (año / equipo / comercial). Cada sub-componente
// se renderiza con `embedded={true}` para que oculte su propio topbar.

// ─── HubSpot Page ───

// ─── User Switcher (with PIN confirmation) ───

// ─── Comisiones en vivo Page ───

// ─── Inicio (dashboard del manager / manager_comercial) ───

// Drawer lateral con el desglose de un comercial: comisiones (igual que el
// detalle de Sync Comisiones), bajas y ventas. Datos vienen ya filtrados por
// el periodo activo del ManagerHomePage — el drawer sólo formatea.

// Modal lateral con detalle de cada KPI del Inicio.
// drill = 'comerciales' | 'billing' | 'commPct' | 'commission' | 'refunds' | 'unpaid2' | 'unpaid3' | 'netPay'

// ─── Inicio del comercial: wrapper que reutiliza CommercialHomeView ───
// Garantiza que cuando un comercial entra en su "Inicio" (o cuando un manager
// hace "Ver como…") vea EXACTAMENTE la misma vista gamificada que se muestra
// en la pestaña "Inicio" del perfil. Antes mostraba MiMesPage (vista antigua).

// ─── Inicio (dashboard del comercial · vista antigua, sin uso activo) ───

// ─── My Profile Page (non-manager) ───
// ─── Sugerencias / tickets ───
// Cualquier rol envía un ticket (título + cuerpo). Manager además ve la
// lista completa con estados y puede cambiarlos: pending → planned →
// inProgress → completed (o descartado).
// SUGGESTION_STATUS_META vive ahora en src/common.jsx.

// ─── SimPage ───

// ─── ChangeLogPage ───
// Auditoría automática: cada sincronización + cada CRUD relevante deja una entrada.
// El cron diario garantiza al menos una entrada por día (con "sin cambios" si procede).

// ─── Tour ───
// Tours actualizados a la estructura actual de la app (mayo 2026):
// sidebar: Áreas (Ventas/Impagos/Bajas/Cobros) + Analítica (Historial/Sync/Estadísticas)
// + nuevo Cobros, Ventas, Sugerencias, perfil gamificado del comercial.
// TOUR_STEPS vive ahora en src/common.jsx.

// ─── Alerts banner (manager) ───
// Lee /api/admin/alerts y muestra una banda compacta con los patrones detectados
// (tasas altas de bajas/impagos, saldo deudor, bajas tempranas). Solo informativo:
// los enlaces llevan a la página correspondiente para investigar.

// ─── Sales Page: listado plano de ventas (deals) con estado computado ───
// Cruza syncs.deals + refunds + payments + incomplete-unpaid en el backend
// y muestra: total + cajitas clickables por estado + tabla detallada. Al
// clickar una fila se abre un drawer con el historial de cambios de las
// propiedades clave del deal en HubSpot.
// SALES_ESTADO_META vive ahora en src/common.jsx (lo usa operativa.jsx).

// ─── Cobros Page: tabla pivot mensual con cierre de variable ───
// Meses como columnas; secciones por bloques (facturación → bajas → cobro
// inicial → impagos → cobro final). El manager puede editar el ajuste manual
// por mes/comercial. El saldo pendiente se arrastra al mes siguiente.

// ─── Bajas Page (manager) ───
// Bajas aceptadas (persistidas en refunds.json) + funnel en vivo (Kanban con todos
// los stages del pipeline "Gestión de Bajas"). No escribe en HubSpot.

// ─── Impagos Page (manager) ───

// ─── Modal de onboarding para comerciales nuevos ───
// Se muestra la primera vez que un comercial entra (sin onboardingCompletedAt).
// 4 pasos: bienvenida → tu plan → tu objetivo → tu perfil. El último guarda
// el flag para que no vuelva a salir.

// ─── Theme toggle ───
// 3 modos: auto (sigue prefers-color-scheme), light, dark. Persiste en
// localStorage. Aplica data-theme al <html> para que el CSS reaccione.
function ThemeToggle() {
  const [theme, setTheme] = useState(() => localStorage.getItem('uca-theme') || 'auto');
  useEffect(() => {
    const html = document.documentElement;
    if (theme === 'auto') html.removeAttribute('data-theme');
    else html.setAttribute('data-theme', theme);
    localStorage.setItem('uca-theme', theme);
  }, [theme]);
  const cycle = () => setTheme(t => (t === 'auto' ? 'light' : t === 'light' ? 'dark' : 'auto'));
  const icon = theme === 'light' ? '☀️' : theme === 'dark' ? '🌙' : '🌓';
  const label = theme === 'light' ? 'Claro' : theme === 'dark' ? 'Oscuro' : 'Auto';
  return (
    <div
      onClick={cycle}
      title={`Tema: ${label} (clic para cambiar)`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 10px',
        borderRadius: 6,
        cursor: 'pointer',
        color: 'rgba(255,255,255,.55)',
        fontSize: 12,
        fontWeight: 500,
        border: '1px solid rgba(255,255,255,.09)',
        transition: 'all .15s',
        userSelect: 'none',
        marginTop: 6,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.color = 'rgba(255,255,255,.9)';
        e.currentTarget.style.background = 'rgba(255,255,255,.07)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.color = 'rgba(255,255,255,.55)';
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>
      <span>{label}</span>
    </div>
  );
}

// ─── App ───
function App() {
  const [activeUser, setActiveUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('uca_user') || 'null');
    } catch {
      return null;
    }
  });
  // Si hay sesión activa, aterrizar en Inicio (dashboard de su rol); si no, en calc para que se vea login.
  const [page, setPage] = useState(activeUser ? 'inicio' : 'calc');
  // Track page_view via useEffect — efectos secundarios NO van dentro del state
  // updater (React lo considera un setState "impuro" y produce el error #300
  // "Should not already be working" al re-renderizar tras logout).
  useEffect(() => {
    if (!activeUser) return undefined;
    track('page_view', { page });
    return undefined;
  }, [page, activeUser?.id]);
  const [cats, setCats] = useState([]);
  const [users, setUsers] = useState([]);
  const [histKey, setHistKey] = useState(0);
  const [calcPreload, setCalcPreload] = useState(null);
  const [profileUser, setProfileUser] = useState(null); // user being edited in profile page
  const [teams, setTeams] = useState([]);
  const [tourActive, setTourActive] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null); // manager impersonating another user

  // Defensa: si el endpoint devuelve 401/error, `r.json()` resuelve con el
  // body de error (un objeto), no un array. Sin coercionar, los consumidores
  // (`cats.map`, `users.filter`…) petan y tumban la sección. Coercionamos a [].
  const loadCats = useCallback(async () => {
    try {
      const r = await fetch('/api/categories');
      const d = await r.json();
      setCats(Array.isArray(d) ? d : []);
    } catch {}
  }, []);
  const loadUsers = useCallback(async () => {
    try {
      const r = await fetch('/api/users');
      const d = await r.json();
      setUsers(Array.isArray(d) ? d : []);
    } catch {}
  }, []);
  const loadTeams = useCallback(async () => {
    try {
      const r = await fetch('/api/teams');
      const d = await r.json();
      setTeams(Array.isArray(d) ? d : []);
    } catch {}
  }, []);

  useEffect(() => {
    if (activeUser) {
      loadCats();
      loadUsers();
      loadTeams();
    }
  }, [activeUser]);

  // ── Validación de sesión server-side (lote-92) ──
  // localStorage es solo un caché para render rápido al arrancar; la fuente
  // de verdad es la cookie HttpOnly + /api/auth/me. Al montar:
  //   - Si /me devuelve 200, sincronizamos activeUser con el server (puede
  //     haber cambiado el rol u otros campos desde la última sesión).
  //   - Si /me devuelve 401, la sesión expiró → limpiamos localStorage y
  //     forzamos login. No esperamos al next clic — el usuario ve el form
  //     en vez de quedarse navegando una UI rota.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/auth/me');
        if (cancelled) return;
        if (r.ok) {
          const u = await r.json();
          // Solo actualizamos si cambió algo — evita re-renders innecesarios.
          const cached = (() => {
            try {
              return JSON.parse(localStorage.getItem('uca_user') || 'null');
            } catch {
              return null;
            }
          })();
          if (!cached || JSON.stringify(cached) !== JSON.stringify(u)) {
            try {
              localStorage.setItem('uca_user', JSON.stringify(u));
            } catch {}
            setActiveUser(u);
          }
        } else if (r.status === 401) {
          // Sesión inválida / expirada / nunca existió. Si tenemos algo en
          // localStorage es stale — limpiar y forzar login.
          if (activeUser) {
            try {
              localStorage.removeItem('uca_user');
            } catch {}
            setActiveUser(null);
            setPage('calc');
          }
        }
      } catch {
        // Network/server down — dejamos lo que haya en localStorage. La app
        // seguirá funcionando degradada (sin endpoints) hasta que vuelva.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Solo al montar — no queremos re-validar cada vez que cambia activeUser
    // porque handleLogin/Logout ya actualizan localStorage explícitamente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Telemetría de sesión: track session_start si hay sesión recuperada del
  // localStorage al cargar la app (no pasa por handleLogin). Track session_end
  // en beforeunload para capturar cierres de pestaña.
  useEffect(() => {
    if (!activeUser) return undefined;
    track('session_start', { role: activeUser.role, restored: true });
    const onBeforeUnload = () => track('session_end');
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser?.id]);

  // When impersonating, the effective user determines what is visible
  const effectiveUser = impersonatedUser || activeUser;
  const isViewer = effectiveUser?.role === 'viewer';
  const isOperaciones = effectiveUser?.role === 'operaciones';
  // Viewer ve TODO lo que vería un manager, pero sin permisos de edición.
  // Operaciones NO debe tener acceso: el backend bloquea su login con 403, y
  // al impersonarlo mostramos una pantalla de "sin acceso" más abajo.
  // isManager basa el branching en el rol EFECTIVO (no en el real) para que
  // impersonar a un viewer dé la vista de manager (read-only) y no la
  // comercial.
  // isManager: rol efectivo que ve la UI de manager (sidebar de gestión, etc.).
  // Incluye:
  //   - manager      → "Admin" (rol antiguo "RO/Responsable Operaciones")
  //   - super_admin  → Ramiro, hereda todo
  //   - viewer       → manager read-only
  //   - ro           → Revenue Owner (lote-87): UI de manager pero scoped a su
  //                    teamId (FP/Opos/Acceso Uni). Vive aquí dentro para
  //                    reutilizar todo el render — el scope se aplica vía
  //                    `managedTeamId` que ahora también lo setea el RO con
  //                    su propio teamId.
  // super_admin hereda la UI completa de Admin — el flag adicional
  // isSuperAdmin gatea las acciones destructivas (borrar usuarios/planes,
  // disparar Sync de comisiones).
  const isAdmin = effectiveUser?.role === 'manager';
  const isRO = effectiveUser?.role === 'ro';
  const isManager =
    isAdmin || isRO || effectiveUser?.role === 'viewer' || effectiveUser?.role === 'super_admin';
  const isManagerComercial = effectiveUser?.role === 'manager_comercial';
  const isSuperAdmin = effectiveUser?.role === 'super_admin';
  // managedTeamId: ID del team al que se restringe la vista para roles
  // scoped (TL y RO). Para Admin/super_admin/viewer no aplica (ven todo).
  // Cualquier página que reciba `lockedTeamId={managedTeamId}` filtrará
  // automáticamente — el mismo patrón que ya usaba TL.
  const managedTeamId = isManagerComercial || isRO ? effectiveUser?.teamId : null;
  // realIsAdmin: Admin/super_admin REAL (no impersonado). Para gatear acciones
  // que SOLO un Admin real puede hacer: impersonar a cualquiera, ver botones
  // que un viewer impersonado no debería tocar.
  const realIsAdmin = activeUser?.role === 'manager' || activeUser?.role === 'super_admin';
  // realIsRO: Revenue Owner real (no impersonado). Puede usar "Ver como" pero
  // solo a gente de SU área (teamId).
  const realIsRO = activeUser?.role === 'ro';
  // realIsTL: TL real (manager_comercial sin impersonar). Puede usar "Ver como"
  // pero limitado a los comerciales de SU equipo (filterTeamId fuerza el scope).
  const realIsTL = activeUser?.role === 'manager_comercial';
  // realIsManager: alias retrocompatible — antes era "manager o super_admin".
  // Mantenido para no romper checks dispersos en la codebase. RO va aparte
  // porque tiene scope distinto.
  const realIsManager = realIsAdmin;
  // canImpersonate: cualquier rol real con permisos de gestión puede usar
  // "Ver como" — Admin/super_admin sin restricción, RO solo su área, TL solo
  // su equipo.
  const canImpersonate = (realIsAdmin || realIsRO || realIsTL) && !impersonatedUser;
  // isReadOnly: cualquier UI de edición debe deshabilitar acciones aquí.
  const isReadOnly = isViewer;
  // lote-183 (1.4): estado de cierre del MES EN CURSO para el indicador del
  // ítem "Cierre" del lateral. Fetch ligero (no importamos closures.jsx para
  // no engordar el bundle de entrada). Se refresca al cambiar de página, así
  // que tras cerrar el mes el punto pasa a verde al volver.
  const [curMonthClosures, setCurMonthClosures] = useState([]);
  useEffect(() => {
    if (!activeUser) return;
    const d = new Date();
    fetch(`/api/closures?year=${d.getFullYear()}&month=${d.getMonth() + 1}`, {
      credentials: 'include',
    })
      .then(r => (r.ok ? r.json() : []))
      .then(list => setCurMonthClosures(Array.isArray(list) ? list : []))
      .catch(() => setCurMonthClosures([]));
  }, [activeUser?.id, page]);
  const currentMonthClosed =
    curMonthClosures.some(c => !c.reopenedAt && c.scope === 'global') ||
    (!!managedTeamId &&
      curMonthClosures.some(
        c => !c.reopenedAt && c.scope === 'team' && c.scopeId === managedTeamId
      ));
  // can(action): consulta la matriz de capacidades centralizada definida
  // arriba (ROLE_CAPABILITIES). Usa el rol EFECTIVO para que un manager
  // que impersona un viewer pase a tener capacidades read-only.
  const cap = action => can(effectiveUser?.role, action);

  const landingFor = () => 'inicio'; // todos los roles aterrizan en su dashboard de Inicio

  const handleLogin = user => {
    // Escribimos localStorage ANTES de track() para que el helper pueda leer
    // el activeUser. Luego programamos los setState — React batchea.
    try {
      localStorage.setItem('uca_user', JSON.stringify(user));
    } catch {}
    track('session_start', { role: user.role });
    setActiveUser(user);
    setPage(landingFor(user.role));
  };

  const handleLogout = () => {
    track('session_end');
    // lote-92: invalidamos la sesión server-side antes de limpiar local.
    // Fire-and-forget: no esperamos la respuesta para no dejar la UI
    // congelada si la red está mal. El servidor borra el cookie via Set-Cookie
    // y nuestro cliente la deja de mandar igualmente al limpiar local.
    fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setActiveUser(null);
    localStorage.removeItem('uca_user');
    setPage('calc');
  };

  const switchUser = user => {
    setActiveUser(user);
    localStorage.setItem('uca_user', JSON.stringify(user));
    setPage(landingFor(user.role));
    setCalcPreload(null);
  };

  const loadEntry = entry => {
    setCalcPreload(entry);
    setPage('calc');
  };

  // If current page is not accessible for this role, redirect
  useEffect(() => {
    // Páginas reservadas a manager + manager_comercial (bajas e impagos las ven todos, cada uno con su alcance)
    const teamScopedOnly = page === 'users' || page === 'changelog' || page === 'config';
    if (teamScopedOnly && !(isManager || isManagerComercial)) return setPage('inicio');
    // Changelog requiere permisos de edición (viewer no lo ve). Config sí
    // lo ve un viewer pero en modo lectura (sin botones de edición).
    if (page === 'changelog' && isReadOnly) return setPage('inicio');
  }, [isManager, isManagerComercial, isReadOnly, page]);

  // CRÍTICO — todos los hooks (useState/useEffect/etc.) deben declararse ANTES
  // de cualquier return condicional. Si declaramos hooks después del `if
  // (!activeUser) return <LoginPage/>`, al hacer logout React renderiza menos
  // hooks que antes y dispara error #300 ("Should not already be working") +
  // pantalla en blanco. Por eso esos states viven aquí arriba.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [simulatingOnboarding, setSimulatingOnboarding] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [pendingSaleDealId, setPendingSaleDealId] = useState(null);
  useEffect(() => {
    if (!activeUser) return undefined;
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeUser]);

  // Handle password reset link
  const resetToken = new URLSearchParams(window.location.search).get('reset_token');
  if (resetToken) return <ResetPinPage token={resetToken} />;

  if (!activeUser) return <LoginPage onLogin={handleLogin} />;

  // ── Rotación forzada de contraseña (lote-93) ──
  // Si el server marcó al user con requiresPasswordReset (porque tiene PIN
  // antiguo legacy SHA-256 o porque tecleó algo débil), mostramos un modal
  // bloqueante que NO se puede cerrar hasta que cumpla la política. La app
  // no se renderiza detrás — el user no puede usar nada sin actualizar.
  if (activeUser.requiresPasswordReset) {
    const handleChangePassword = async ({ newPin }) => {
      const r = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPin }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        // Si hay códigos de regla incumplida, los enseñamos en humano.
        const codeMap = {
          too_short: 'mínimo 10 caracteres',
          no_letter: 'falta letra',
          no_digit: 'falta dígito',
          no_symbol: 'falta símbolo',
          common: 'es una contraseña común',
          sequential: 'es secuencia obvia',
          all_same: 'todos los caracteres iguales',
        };
        const detail = Array.isArray(data.errors)
          ? data.errors.map(c => codeMap[c] || c).join(', ')
          : '';
        throw new Error((data.error || 'No se pudo actualizar') + (detail ? ': ' + detail : ''));
      }
      // Éxito — limpiar el flag local y refrescar el user desde /me para
      // tener la versión canónica (sin flag).
      const meRes = await fetch('/api/auth/me');
      if (meRes.ok) {
        const u = await meRes.json();
        try {
          localStorage.setItem('uca_user', JSON.stringify(u));
        } catch {}
        setActiveUser(u);
      } else {
        // Edge: por si /me falla, al menos quitamos el flag local
        const next = { ...activeUser, requiresPasswordReset: false };
        try {
          localStorage.setItem('uca_user', JSON.stringify(next));
        } catch {}
        setActiveUser(next);
      }
    };
    return (
      <div className="ov" style={{ zIndex: 1100 }}>
        <div className="modal ani" style={{ maxWidth: 480, padding: 0 }}>
          <div style={{ padding: '24px 28px 8px' }}>
            <div style={{ fontSize: 38, textAlign: 'center', marginBottom: 8 }}>🔒</div>
            <h2 style={{ textAlign: 'center', margin: '0 0 8px', fontSize: 18 }}>
              Crea una contraseña más segura
            </h2>
            <p
              style={{
                textAlign: 'center',
                color: 'var(--tx2)',
                fontSize: 13,
                margin: '0 0 4px',
                lineHeight: 1.5,
              }}
            >
              Hemos endurecido la política de contraseñas. Tu contraseña actual no cumple los nuevos
              requisitos y tienes que crear una nueva para seguir usando la app.
            </p>
            <p
              style={{
                textAlign: 'center',
                color: 'var(--tx-mu)',
                fontSize: 11,
                margin: '0 0 14px',
                lineHeight: 1.5,
              }}
            >
              <strong>{activeUser.name}</strong> · {activeUser.email}
            </p>
          </div>
          <div style={{ padding: '0 28px 22px' }}>
            <PasswordSet onSave={handleChangePassword} submitLabel="Actualizar contraseña" />
          </div>
          {/* Botón de logout para users que prefieren cerrar sesión y volver
              luego (p.ej. son admin viendo el problema en otra cuenta) */}
          <div
            style={{ borderTop: '1px solid var(--bds)', padding: '10px 28px', textAlign: 'center' }}
          >
            <button
              onClick={handleLogout}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--tx-mu)',
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              ¿No es tu cuenta? Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  const startImpersonate = u => {
    // Guard scope: TL solo puede ver como gente de su propio equipo.
    // El frontend ya filtra el listado (filterTeamId) pero defendemos aquí
    // por si alguien llega vía deep-link o atajo.
    if (realIsTL && u.teamId !== activeUser.teamId) {
      alert('Como Team Leader solo puedes ver como usuarios de tu equipo');
      return;
    }
    // Bloqueo extra: TL no puede ver como otro manager/super_admin —
    // sería elevación de privilegios.
    if (
      realIsTL &&
      (u.role === 'manager' ||
        u.role === 'super_admin' ||
        u.role === 'manager_comercial' ||
        u.role === 'ro')
    ) {
      alert('Como Team Leader solo puedes ver como comerciales de tu equipo');
      return;
    }
    // Mismo patrón para RO: scope a su área (teamId) — no puede ver como
    // gente de otra área ni elevar a Admin/super_admin.
    if (realIsRO && u.teamId !== activeUser.teamId) {
      alert('Como Revenue Owner solo puedes ver como usuarios de tu área');
      return;
    }
    if (realIsRO && (u.role === 'manager' || u.role === 'super_admin')) {
      alert('Como Revenue Owner no puedes ver como Admin/Super Admin');
      return;
    }
    setImpersonatedUser(u);
    setPage(landingFor(u.role));
    setCalcPreload(null);
  };
  const stopImpersonate = () => {
    setImpersonatedUser(null);
    setPage('users');
  };

  const visibleCats =
    isManager || impersonatedUser
      ? cats
      : cats.filter(c => (effectiveUser?.assignedCategoryIds || []).includes(c.id));

  // Impersonar a un user con rol 'operaciones' debe mostrar la pantalla de
  // "sin acceso" — replicamos lo que vería en un login real (que está
  // bloqueado por el backend con 403). Sólo se llega aquí vía impersonación
  // de un manager real.
  if (impersonatedUser && isOperaciones) {
    return (
      <div className="layout">
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999,
            background: 'var(--pu)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 20px',
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 2px 12px rgba(0,0,0,.18)',
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: 15, height: 15, flexShrink: 0 }}
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span>
            Viendo como: <strong>{impersonatedUser.name}</strong>
          </span>
          <span
            style={{
              opacity: 0.7,
              fontWeight: 400,
              fontSize: 11,
              background: 'rgba(255,255,255,.15)',
              borderRadius: 10,
              padding: '1px 8px',
            }}
          >
            operaciones
          </span>
          <button
            onClick={stopImpersonate}
            style={{
              marginLeft: 'auto',
              background: 'rgba(255,255,255,.18)',
              border: '1.5px solid rgba(255,255,255,.35)',
              color: '#fff',
              borderRadius: 6,
              padding: '4px 12px',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            ✕ Volver a vista de manager
          </button>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '60px 20px',
            textAlign: 'center',
            marginTop: 36,
          }}
        >
          <div style={{ fontSize: 56, marginBottom: 14 }}>🚫</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
            Sin acceso a la plataforma
          </h1>
          <p style={{ fontSize: 14, color: 'var(--tx-mu)', maxWidth: 440, lineHeight: 1.6 }}>
            Los usuarios con rol <strong>Operaciones</strong> no pueden entrar a la app de
            Comisiones. Este es el aspecto que verían si intentaran iniciar sesión.
          </p>
        </div>
      </div>
    );
  }

  // showOnboarding deriva del state declarado al inicio del componente.
  const showOnboarding =
    simulatingOnboarding ||
    (!!effectiveUser &&
      effectiveUser.role === 'comercial' &&
      !effectiveUser.onboardingCompletedAt &&
      !onboardingDismissed &&
      !impersonatedUser); // si un manager está impersonando, NO sale el onboarding
  const onSearchSelect = sale => {
    setSearchOpen(false);
    setPendingSaleDealId(sale.dealId);
    setPage('sales');
  };
  return (
    <div className={'layout' + (sidebarOpen ? ' sidebar-open' : '')}>
      {/* Botón hamburguesa: visible sólo en mobile via CSS .mobile-burger. */}
      <button className="mobile-burger" onClick={() => setSidebarOpen(o => !o)} aria-label="Menú">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
        >
          {sidebarOpen ? (
            <>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </>
          ) : (
            <>
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="17" x2="20" y2="17" />
            </>
          )}
        </svg>
      </button>
      {/* Overlay para cerrar al tocar fuera del sidebar en mobile. */}
      <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      {/* Búsqueda global de ventas (Cmd/Ctrl+K) */}
      {searchOpen && (
        <GlobalSearch onSelect={onSearchSelect} onClose={() => setSearchOpen(false)} />
      )}
      {/* Onboarding del comercial nuevo (solo se muestra una vez). En modo
          simulación (manager desde Config) NO se persiste nada — el botón
          cierra el modal sin tocar al user. */}
      {showOnboarding && (
        <OnboardingModal
          user={effectiveUser}
          cats={cats}
          onComplete={patch => {
            if (simulatingOnboarding) {
              setSimulatingOnboarding(false);
              return;
            }
            setOnboardingDismissed(true);
            // Reflejamos cambios en el activeUser local (avatar) sin esperar al reload.
            setActiveUser(u =>
              u ? { ...u, ...patch, onboardingCompletedAt: new Date().toISOString() } : u
            );
            localStorage.setItem(
              'uca_user',
              JSON.stringify({
                ...effectiveUser,
                ...patch,
                onboardingCompletedAt: new Date().toISOString(),
              })
            );
            loadUsers();
          }}
          onSkip={() => {
            if (simulatingOnboarding) {
              setSimulatingOnboarding(false);
              return;
            }
            setOnboardingDismissed(true);
            setActiveUser(u => (u ? { ...u, onboardingCompletedAt: new Date().toISOString() } : u));
            localStorage.setItem(
              'uca_user',
              JSON.stringify({ ...effectiveUser, onboardingCompletedAt: new Date().toISOString() })
            );
          }}
        />
      )}
      {impersonatedUser && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999,
            background: 'var(--pu)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 20px',
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 2px 12px rgba(0,0,0,.18)',
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: 15, height: 15, flexShrink: 0 }}
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span>
            Viendo como: <strong>{impersonatedUser.name}</strong>
          </span>
          <span
            style={{
              opacity: 0.85,
              fontWeight: 600,
              fontSize: 11,
              background: 'rgba(255,255,255,.15)',
              borderRadius: 10,
              padding: '1px 8px',
            }}
          >
            {{
              super_admin: 'Super Admin',
              manager: 'Admin',
              ro: 'RO',
              manager_comercial: 'TL',
              comercial: 'Comercial',
              operaciones: 'Operaciones',
              viewer: 'Viewer',
            }[impersonatedUser.role] || impersonatedUser.role}
          </span>
          <button
            onClick={stopImpersonate}
            style={{
              marginLeft: 'auto',
              background: 'rgba(255,255,255,.18)',
              border: '1.5px solid rgba(255,255,255,.35)',
              color: '#fff',
              borderRadius: 6,
              padding: '4px 12px',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'background .15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.28)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,.18)')}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              style={{ width: 12, height: 12 }}
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Salir
          </button>
        </div>
      )}
      <aside
        className="sidebar"
        style={impersonatedUser ? { marginTop: 36 } : {}}
        onClick={e => {
          if (e.target.closest('.sb-item')) setSidebarOpen(false);
        }}
      >
        {/* Logo se moved al footer del sidebar para ganar espacio vertical arriba. */}
        <div className="sb-nav">
          {/* Búsqueda global de ventas (atajo Cmd/Ctrl+K). */}
          <div
            className="sb-item"
            onClick={() => setSearchOpen(true)}
            style={{ marginBottom: 8, background: 'rgba(255,255,255,.06)' }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <span style={{ flex: 1 }}>Buscar venta</span>
            <span
              style={{
                fontSize: 10,
                padding: '2px 6px',
                background: 'rgba(255,255,255,.12)',
                borderRadius: 4,
                color: 'rgba(255,255,255,.7)',
                fontFamily: 'inherit',
              }}
            >
              ⌘K
            </span>
          </div>
          {/* Inicio — dashboard de cada rol */}
          <div
            className={`sb-item ${page === 'inicio' ? 'on' : ''}`}
            onClick={() => setPage('inicio')}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12L12 3l9 9" />
              <path d="M5 10v10h14V10" />
            </svg>
            <span>Inicio</span>
          </div>
          {isReadOnly && (
            <div
              style={{
                margin: '12px 14px 4px',
                padding: '8px 10px',
                background: 'rgba(255,255,255,.07)',
                borderRadius: 8,
                fontSize: 11,
                color: 'rgba(255,255,255,.7)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 14 }}>👁️</span>
              <span>
                <strong style={{ color: '#fff' }}>Modo lectura</strong> — sin permisos de edición
              </span>
            </div>
          )}
          {/* Áreas: las pantallas operativas del día a día (ver/gestionar). */}
          <div className="sb-lbl" style={{ marginTop: 0 }}>
            Áreas
          </div>
          <div
            className={`sb-item ${page === 'sales' ? 'on' : ''}`}
            onClick={() => setPage('sales')}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>Ventas</span>
          </div>
          <div
            className={`sb-item ${page === 'cobros' ? 'on' : ''}`}
            onClick={() => setPage('cobros')}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 1v22" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            <span>Cobros</span>
          </div>
          {/* Simulador: pertenece a Áreas (uso operativo, no exclusivo del rol).
              Antes estaba bajo "Mi área". */}
          <div className={`sb-item ${page === 'sim' ? 'on' : ''}`} onClick={() => setPage('sim')}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="4" y1="21" x2="4" y2="14" />
              <line x1="4" y1="10" x2="4" y2="3" />
              <line x1="12" y1="21" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12" y2="3" />
              <line x1="20" y1="21" x2="20" y2="16" />
              <line x1="20" y1="12" x2="20" y2="3" />
              <line x1="1" y1="14" x2="7" y2="14" />
              <line x1="9" y1="8" x2="15" y2="8" />
              <line x1="17" y1="16" x2="23" y2="16" />
            </svg>
            <span>Simulador</span>
          </div>
          {/* Bajas e Impagos ya no aparecen aquí — viven como tabs dentro
              de Estadísticas. Mantenemos page==='bajas'/'impagos' como
              alias compatibles (redirigen al wrapper). */}
          {/* Analítica: pantallas de lectura / comparativas. */}
          <div className="sb-lbl" style={{ marginTop: 10 }}>
            Analítica
          </div>
          {/* lote-197: Ranking como página propia, encima de Estadísticas. */}
          <div
            className={`sb-item ${page === 'ranking' ? 'on' : ''}`}
            onClick={() => setPage('ranking')}
          >
            <span style={{ fontSize: 16, width: 16, textAlign: 'center' }}>🏆</span>
            <span>Ranking</span>
          </div>
          {/* Historial + Estadísticas: una sola entrada que abre la página
              unificada (HistAnalyticsPage) con tabs internos. */}
          <div
            className={`sb-item ${page === 'hist' || page === 'analytics' || page === 'bajas' || page === 'impagos' ? 'on' : ''}`}
            onClick={() => setPage('hist')}
          >
            {Ic.hist}
            <span>Estadísticas</span>
          </div>
          <div
            className={`sb-item ${page === 'carrera' ? 'on' : ''}`}
            onClick={() => setPage('carrera')}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="8" r="7" />
              <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
            </svg>
            <span>Carrera</span>
          </div>
          <div className="sb-lbl" style={{ marginTop: 10 }}>
            Mi área
          </div>
          {/* Calculadora oculta del menú — el Simulador cubre el mismo caso de uso.
              Mantenemos la ruta 'calc' por si se accede vía botón "Editar" desde Historial.
              Simulador se movió a la sección "Áreas". */}
          <div
            className={`sb-item ${page === 'profile' ? 'on' : ''}`}
            onClick={() => setPage('profile')}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span>Mi perfil</span>
          </div>
          {/* lote-205: Sugerencias OCULTO para todos los roles (no borrado). La
              ruta `suggestions` y SuggestionsPage siguen existiendo; solo se
              retira el acceso desde el sidebar. Reactivar = SHOW_SUGGESTIONS_NAV. */}
          {SHOW_SUGGESTIONS_NAV && (
            <div
              className={`sb-item ${page === 'suggestions' ? 'on' : ''}`}
              onClick={() => setPage('suggestions')}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
              <span>{isManager ? 'Sugerencias' : 'Enviar sugerencia'}</span>
            </div>
          )}
          {/* Sección Manager: visible para manager y manager_comercial. Va al
              FINAL del sidebar — accesos administrativos, menos frecuentes. */}
          {(isManager || isManagerComercial) && (
            <>
              {/* Lote-134: sidebar reorganizado.
                  - "Mi equipo" agrupa el trabajo diario del manager/TL
                    (gestión de personas).
                  - "Configuración" abajo agrupa la administración de la app
                    (planes, sync, logs). */}
              <div className="sb-lbl" style={{ marginTop: 10 }}>
                Mi equipo
              </div>
              <div
                className={`sb-item ${page === 'users' ? 'on' : ''}`}
                onClick={() => setPage('users')}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>Equipo</span>
              </div>
              {/* Lote-155: Objetivos como página dedicada en "Mi equipo". */}
              <div
                data-tour="nav-objetivos"
                className={`sb-item ${page === 'team-objectives' ? 'on' : ''}`}
                onClick={() => setPage('team-objectives')}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
                <span>Objetivos</span>
              </div>
              {/* Lote-156: Cierre mensual como página dedicada en "Mi equipo". */}
              <div
                data-tour="nav-cierre"
                className={`sb-item ${page === 'month-closure' ? 'on' : ''}`}
                onClick={() => setPage('month-closure')}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                  <path d="M9 16l2 2 4-4" />
                </svg>
                <span>Cierre</span>
                {/* lote-183 (1.4): indicador del cierre del mes en curso.
                    Verde ✓ = cerrado · punto rojo = sin cerrar. */}
                {currentMonthClosed ? (
                  <span
                    title="Mes en curso cerrado"
                    style={{
                      marginLeft: 'auto',
                      color: page === 'month-closure' ? '#fff' : 'var(--gr-d)',
                      fontWeight: 700,
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    ✓
                  </span>
                ) : (
                  <span
                    title="Mes en curso sin cerrar"
                    style={{
                      marginLeft: 'auto',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'var(--er)',
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>
              {/* lote-205: Planes y Tramos unificados en un único item que abre
                  la página con dos pestañas. Visible para Admin/RO/TL; la
                  pestaña Tramos dentro solo la ven Admin/RO. */}
              <div
                className={`sb-item ${page === 'planes' || page === 'tramos' ? 'on' : ''}`}
                onClick={() => setPage('planes')}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
                <span>Planes y tramos</span>
              </div>
              {isManager && (
                <>
                  <div className="sb-lbl" style={{ marginTop: 10 }}>
                    Configuración
                  </div>
                  <div
                    className={`sb-item ${page === 'config' ? 'on' : ''}`}
                    onClick={() => setPage('config')}
                  >
                    {Ic.cog}
                    <span>Configuración</span>
                  </div>
                </>
              )}
              {!isReadOnly && (
                <div
                  className={`sb-item ${page === 'hubspot' ? 'on' : ''}`}
                  onClick={() => setPage('hubspot')}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  <span>Sync comisiones</span>
                </div>
              )}
              {!isReadOnly && (
                <div
                  className={`sb-item ${page === 'changelog' ? 'on' : ''}`}
                  onClick={() => setPage('changelog')}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  <span>Log de cambios</span>
                </div>
              )}
              {isSuperAdmin && (
                <div
                  className={`sb-item ${page === 'usage' ? 'on' : ''}`}
                  onClick={() => setPage('usage')}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="20" x2="18" y2="10" />
                    <line x1="12" y1="20" x2="12" y2="4" />
                    <line x1="6" y1="20" x2="6" y2="14" />
                  </svg>
                  <span>Uso de la app</span>
                </div>
              )}
              {/* Lote-114: demo del Inicio comercial v2. Solo visible para super_admin
                hasta que validemos el diseño. Una vez aprobado, sustituye al actual. */}
              {isSuperAdmin && (
                <div
                  className={`sb-item ${page === 'demo-comercial-v2' ? 'on' : ''}`}
                  onClick={() => setPage('demo-comercial-v2')}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span>Demo Inicio v2</span>
                </div>
              )}
            </>
          )}
        </div>
        <div style={{ padding: '8px 10px 4px' }}>
          <div
            onClick={() => {
              // Dispara el tour nuevo (sistema TOURS catalog) si la página
              // tiene uno aplicable. Fallback al tour legacy (TOUR_STEPS)
              // si nadie escucha el evento.
              let handled = false;
              const onceHandled = () => {
                handled = true;
              };
              window.addEventListener('tour:relaunch-ack', onceHandled, { once: true });
              window.dispatchEvent(new CustomEvent('tour:relaunch'));
              setTimeout(() => {
                window.removeEventListener('tour:relaunch-ack', onceHandled);
                if (!handled) setTourActive(v => !v);
              }, 50);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 10px',
              borderRadius: 6,
              cursor: 'pointer',
              color: 'rgba(255,255,255,.38)',
              fontSize: 12,
              fontWeight: 500,
              border: '1px solid rgba(255,255,255,.09)',
              transition: 'all .15s',
              userSelect: 'none',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = 'rgba(255,255,255,.8)';
              e.currentTarget.style.background = 'rgba(255,255,255,.07)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = 'rgba(255,255,255,.38)';
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <span style={{ fontSize: 15, lineHeight: 1 }}>?</span>
            <span>Tour de la sección</span>
          </div>
          <ThemeToggle />
        </div>
        <UserSwitcher
          users={users}
          activeUser={activeUser}
          onSwitch={switchUser}
          onLogout={handleLogout}
        />
        {/* Branding al fondo, discreto. */}
        <div className="sb-logo" style={{ marginTop: 8, opacity: 0.5 }}>
          <div
            className="sb-icon"
            style={{ width: 22, height: 22, background: 'rgba(255,255,255,.08)' }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: 14, height: 14 }}
            >
              <polyline points="3,17 7,11 12,14 17,6 21,9" />
              <polyline points="17,6 21,6 21,10" />
            </svg>
          </div>
          <div style={{ fontSize: 11 }}>
            <strong>Ucademy</strong>
            <span> · Comisiones</span>
          </div>
        </div>
      </aside>
      <div className="main" style={impersonatedUser ? { marginTop: 36 } : {}}>
        <PageErrorBoundary resetKey={page}>
          <Suspense
            fallback={
              <div style={{ padding: 24, opacity: 0.6, fontSize: 13 }}>Cargando módulo…</div>
            }
          >
            {page === 'config' && isManager && (
              <ConfigPage
                cats={cats}
                reload={loadCats}
                teams={teams}
                reloadTeams={loadTeams}
                mode="full"
                activeUser={effectiveUser}
                isReadOnly={isReadOnly}
                onSimulateOnboarding={() => setSimulatingOnboarding(true)}
              />
            )}
            {/* lote-205: Planes y Tramos unificados en una sola página con dos
                pestañas (Planes | Tramos). Antes eran dos items de sidebar.
                Admin/RO/super_admin editan planes; TL (manager_comercial) solo
                lectura. La pestaña Tramos solo la ven Admin/RO (canSeeTramos).
                Ambas rutas ('planes' y 'tramos') abren la misma página; la
                segunda solo fija la pestaña inicial (back-compat de deep links). */}
            {(page === 'planes' || page === 'tramos') && (isManager || isManagerComercial) && (
              <PlanesTramosPage
                initialTab={page === 'tramos' ? 'tramos' : 'planes'}
                canSeeTramos={isManager}
                cats={cats}
                reload={loadCats}
                teams={teams}
                reloadTeams={loadTeams}
                allUsers={users}
                activeUser={effectiveUser}
                canEditPlanes={isManager && !isReadOnly}
                isSuperAdmin={isSuperAdmin}
                isReadOnly={isReadOnly}
              />
            )}
            {page === 'calc' && (
              <CalcPage
                cats={visibleCats}
                activeUser={effectiveUser}
                onSaved={() => setHistKey(k => k + 1)}
                preload={calcPreload}
              />
            )}
            {/* Historial + Estadísticas unificados — TODOS los roles ven el
            wrapper con las 4 pestañas (General, Historial, Bajas, Impagos).
            HistAnalyticsPage respeta viewScope:
              - 'all'  → manager: ve todo, puede filtrar
              - 'team' → manager_comercial: solo su equipo
              - 'own'  → comercial: forzado a su propio userId */}
            {(page === 'hist' ||
              page === 'analytics' ||
              page === 'bajas' ||
              page === 'impagos') && (
              <HistAnalyticsPage
                key={histKey}
                activeUser={effectiveUser}
                viewScope={isManager ? 'all' : isManagerComercial ? 'team' : 'own'}
                managedTeamId={managedTeamId}
                onLoadEntry={loadEntry}
                allUsers={users}
                teams={teams}
                isManager={isManager}
                isManagerComercial={isManagerComercial}
                isReadOnly={isReadOnly}
                initialTab={page === 'bajas' ? 'bajas' : page === 'impagos' ? 'impagos' : null}
                goTo={(p, opts) => {
                  if (opts?.dealId) setPendingSaleDealId(opts.dealId);
                  setPage(p);
                }}
              />
            )}
            {/* lote-197: Ranking como página propia. */}
            {page === 'ranking' && (
              <RankingPage
                allUsers={users}
                activeUser={effectiveUser}
                scope={isManager ? 'all' : isManagerComercial ? 'team' : 'own'}
                managedTeamId={managedTeamId}
              />
            )}
            {page === 'users' && (isManager || isManagerComercial) && profileUser && (
              <UserProfilePage
                user={profileUser}
                cats={cats}
                teams={teams}
                onBack={() => setProfileUser(null)}
                onSaved={() => {
                  loadUsers();
                  setProfileUser(u => {
                    const fresh = users.find(x => x.id === u?.id);
                    return fresh ? { ...fresh } : null;
                  });
                }}
                canEditRole={realIsManager && !impersonatedUser}
                activeUser={effectiveUser}
                isReadOnly={isReadOnly}
              />
            )}
            {page === 'users' && (isManager || isManagerComercial) && !profileUser && (
              <EquipoPage
                cats={cats}
                allUsers={users}
                reloadUsers={loadUsers}
                setProfileUser={setProfileUser}
                teams={teams}
                onImpersonate={canImpersonate ? startImpersonate : null}
                filterTeamId={managedTeamId}
                isReadOnly={isReadOnly}
                activeUser={effectiveUser}
              />
            )}
            {page === 'team-objectives' && (isManager || isManagerComercial) && (
              <ObjetivosPage
                allUsers={users}
                teams={teams}
                activeUser={effectiveUser}
                lockedTeamId={managedTeamId}
                isManager={isManager}
                isSuperAdmin={isSuperAdmin}
                isReadOnly={isReadOnly}
                onOpenProfile={u => {
                  setProfileUser(u);
                  setPage('users');
                }}
              />
            )}
            {page === 'month-closure' && (isManager || isManagerComercial) && (
              <CierrePage
                allUsers={users}
                teams={teams}
                activeUser={effectiveUser}
                lockedTeamId={managedTeamId}
                isReadOnly={isReadOnly}
              />
            )}
            {page === 'live' && (
              <LivePage
                cats={cats}
                users={users}
                teams={teams}
                activeUser={effectiveUser}
                canSave={isManager || isManagerComercial}
                filterTeamId={managedTeamId}
              />
            )}
            {page === 'hubspot' && (
              <HubspotPage
                cats={cats}
                users={users}
                activeUser={effectiveUser}
                reloadUsers={loadUsers}
                canEdit={isSuperAdmin}
              />
            )}
            {page === 'changelog' && (isManager || isManagerComercial) && !isReadOnly && (
              <ChangeLogPage activeUser={effectiveUser} />
            )}
            {page === 'usage' && isSuperAdmin && (
              <UsagePage activeUser={effectiveUser} teams={teams} />
            )}
            {page === 'demo-comercial-v2' && isSuperAdmin && <DemoComercialV2Page />}
            {page === 'sales' && (
              <SalesPage
                allUsers={users}
                teams={teams}
                lockedTeamId={managedTeamId}
                lockedOwnerId={
                  !isManager && !isManagerComercial ? effectiveUser?.hubspotOwnerId : null
                }
                lockedUserId={!isManager && !isManagerComercial ? effectiveUser?.id : null}
                autoOpenDealId={pendingSaleDealId}
                onAutoOpenConsumed={() => setPendingSaleDealId(null)}
              />
            )}
            {page === 'cobros' && (
              <CobrosPage
                allUsers={users}
                teams={teams}
                lockedTeamId={managedTeamId}
                lockedUserId={!isManager && !isManagerComercial ? effectiveUser?.id : null}
                isManager={isManager}
                activeUser={effectiveUser}
              />
            )}
            {/* Bajas e Impagos viven dentro de HistAnalyticsPage como tabs.
            page==='bajas' y page==='impagos' se renderizan arriba con el
            wrapper de Estadísticas y initialTab='bajas'|'impagos'. */}
            {page === 'inicio' && (isManager || isManagerComercial) && (
              <ManagerHomePage
                allUsers={users}
                teams={teams}
                goTo={setPage}
                setProfileUser={setProfileUser}
                lockedTeamId={managedTeamId}
                isManager={isManager}
                activeUser={effectiveUser}
              />
            )}
            {page === 'inicio' && !isManager && !isManagerComercial && (
              <CommercialHomeV2Page
                activeUser={effectiveUser}
                teams={teams}
                allUsers={users}
                cats={cats}
              />
            )}
            {page === 'carrera' && (
              <CarreraPage
                activeUser={effectiveUser}
                allUsers={users}
                teams={teams}
                cats={cats}
                isManager={isManager}
                isManagerComercial={isManagerComercial}
                lockedTeamId={managedTeamId}
              />
            )}
            {page === 'sim' && (
              <SimPage
                cats={visibleCats}
                activeUser={effectiveUser}
                allUsers={users}
                managedTeamId={managedTeamId}
                teams={teams}
              />
            )}
            {page === 'profile' && (
              <MyProfilePage
                activeUser={effectiveUser}
                cats={cats}
                teams={teams}
                onSaved={loadUsers}
                allUsers={users}
              />
            )}
            {page === 'suggestions' && (
              <SuggestionsPage
                activeUser={effectiveUser}
                isManager={isManager}
                allUsers={users}
                isReadOnly={isReadOnly}
              />
            )}
          </Suspense>
        </PageErrorBoundary>
      </div>
      {tourActive && <TourOverlay page={page} onClose={() => setTourActive(false)} />}
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);
